if (!customElements.get('herbl-review-cards')) {
  customElements.define('herbl-review-cards', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.track = this.querySelector('[data-track]');
      this.viewport = this.querySelector('[data-viewport]');
      this.originals = [...this.querySelectorAll('[data-card]:not([data-clone])')];
      this.count = this.originals.length;
      if (!this.track || !this.viewport || !this.count) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.dataset.carouselEngine = 'centered-grid-v2';
      this.index = 0;
      this.activePhysical = null;
      this.position = 0;
      this.pointerId = null;
      this.loop = this.dataset.loop === 'true' && this.count > 1;
      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.count > 1;
      this.pauseOnHover = this.dataset.pauseOnHover !== 'false';
      this.autoplayDelay = Math.max(3, Number(this.dataset.autoplaySeconds) || 5) * 1000;
      this.motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplayPaused = this.motionPreference.matches;
      this.autoplayToggle = this.querySelector('[data-autoplay-toggle]');
      this.ratingSummary = this.querySelector('.rc-summary');
      this.typingText = this.querySelector('[data-typing-text]');
      this.typingTerms = [...this.querySelectorAll('[data-typing-term]')].map(el => el.textContent.trim()).filter(Boolean);
      this.typingIndex = 0;
      this.typingErasing = true;
      this.inView = false;
      this.hovering = false;
      this.dragging = false;
      this.wheeling = false;
      this.classList.toggle('rc--has-photos', this.originals.some(card => !card.classList.contains('rc__card--video')));
      this.originals.forEach((card, i) => { card.dataset.logical = i; });
      if (this.loop) {
        const cloneSet = () => this.originals.map(card => {
          const copy = card.cloneNode(true);
          copy.dataset.clone = 'true';
          copy.setAttribute('aria-hidden', 'true');
          copy.removeAttribute('data-shopify-editor-block');
          copy.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
          copy.querySelectorAll('button,a,[tabindex]').forEach(el => el.setAttribute('tabindex', '-1'));
          return copy;
        });
        this.track.append(...cloneSet(), ...cloneSet());
        this.classList.add('rc--loop');
      }
      this.cards = [...this.track.querySelectorAll('[data-card]')];
      this.cards.forEach((card, i) => { card.dataset.physical = i; });
      this.period = this.cards.length;
      this.classList.add('rc--enhanced');
      this.viewport.scrollLeft = 0;
      this.viewport.addEventListener('pointerdown', event => this.pointerStart(event), options);
      this.viewport.addEventListener('touchmove', event => {
        if (this.pointerId === null || event.touches.length !== 1) return;
        const touch = event.touches[0];
        const dx = Math.abs(touch.clientX - this.pointerStartX);
        const dy = Math.abs(touch.clientY - this.pointerStartY);
        if (event.cancelable && (this.dragging || (dx >= 7 && dx > dy))) event.preventDefault();
      }, { ...options, passive: false });
      window.addEventListener('pointermove', event => this.pointerMove(event), { ...options, passive: false });
      window.addEventListener('pointerup', event => this.pointerFinish(event), options);
      window.addEventListener('pointercancel', event => this.pointerFinish(event, true), options);
      this.viewport.addEventListener('lostpointercapture', event => this.pointerFinish(event, true), options);
      this.viewport.addEventListener('dragstart', event => event.preventDefault(), options);
      this.viewport.addEventListener('scroll', () => {
        if (this.viewport.scrollLeft) this.viewport.scrollLeft = 0;
      }, options);
      window.addEventListener('blur', () => {
        if (this.pointerId !== null) this.pointerFinish({ pointerId: this.pointerId, type: 'pointercancel' }, true);
      }, options);
      this.viewport.addEventListener('wheel', event => {
        const delta = event.deltaX || (event.shiftKey ? event.deltaY : 0);
        if (!delta || (!event.shiftKey && Math.abs(delta) < Math.abs(event.deltaY))) return;
        event.preventDefault();
        this.cancelMotion();
        this.wheeling = true;
        this.draw(this.constrain(this.position + Math.max(-this.viewport.clientWidth, Math.min(this.viewport.clientWidth, delta)) / this.stride));
        this.setActive(this.nearestLogical());
        this.syncAutoplay();
        clearTimeout(this.wheelTimer);
        this.wheelTimer = setTimeout(() => {
          this.wheeling = false;
          this.moveTo(Math.round(this.position), true, true);
        }, 120);
      }, { ...options, passive: false });
      this.viewport.addEventListener('keydown', event => {
        if (event.target !== this.viewport) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.step(event.key === 'ArrowRight' ? 1 : -1);
        }
        if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.go(event.key === 'Home' ? 0 : this.count - 1);
        }
      }, options);
      this.addEventListener('click', event => {
        if (this.viewport.contains(event.target) && performance.now() < (this.suppressClickUntil || 0)) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, { ...options, capture: true });
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-autoplay-toggle')) {
          this.autoplayPaused = !this.autoplayPaused;
          this.updateAutoplayControl();
          this.syncAutoplay(true);
          this.syncTyping();
          return;
        }
        if (button.hasAttribute('data-prev')) this.step(-1);
        if (button.hasAttribute('data-next')) this.step(1);
        if (button.hasAttribute('data-dot')) this.go(Number(button.dataset.dot));
        let card = button.closest('[data-card]');
        let video = card?.querySelector('video');
        if (!video) return;
        if (button.hasAttribute('data-play-toggle')) {
          if (!video.paused) { video.pause(); return; }
          this.querySelectorAll('video').forEach(other => { if (other !== video) other.pause(); });
          video.muted = false;
          video.volume = 1;
          this.querySelector('[data-status]').textContent = '';
          video.play().catch(() => { this.querySelector('[data-status]').textContent = this.dataset.error; });
        }
        if (button.hasAttribute('data-mute')) {
          video.muted = !video.muted;
          this.videoState(card, video);
        }
      }, options);
      this.cards.forEach(card => {
        const video = card.querySelector('video');
        if (video) ['play', 'pause', 'ended', 'volumechange'].forEach(name => video.addEventListener(name, () => this.videoState(card, video), options));
      });
      this.addEventListener('pointerenter', event => {
        if (this.pauseOnHover && event.pointerType === 'mouse') { this.hovering = true; this.syncAutoplay(); }
      }, options);
      this.addEventListener('pointerleave', () => { this.hovering = false; this.syncAutoplay(); }, options);
      this.addEventListener('focusin', event => {
        const card = event.target.closest('[data-card]');
        if (card && this.pointerId === null) this.moveTo(Number(card.dataset.physical), false);
        this.syncAutoplay();
      }, options);
      this.addEventListener('focusout', () => queueMicrotask(() => this.syncAutoplay()), options);
      this.addEventListener('shopify:block:select', event => {
        const card = event.target.closest('[data-card]:not([data-clone])');
        if (!card || !this.contains(card)) return;
        this.editorSelected = true;
        this.go(Number(card.dataset.logical), false);
        this.syncAutoplay();
      }, options);
      this.addEventListener('shopify:block:deselect', () => { this.editorSelected = false; this.syncAutoplay(true); }, options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.querySelectorAll('video').forEach(video => video.pause());
        this.syncAutoplay();
        this.syncTyping();
      }, options);
      this.motionPreference.addEventListener('change', event => {
        if (event.matches) {
          this.autoplayPaused = true;
          this.go(this.targetLogical ?? this.index, false);
        }
        this.updateAutoplayControl();
        this.syncAutoplay();
        this.syncTyping();
      }, options);
      this.visibilityObserver = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.2;
        this.syncAutoplay();
        this.syncTyping();
      }, { threshold: 0.2 });
      this.visibilityObserver.observe(this.viewport);
      this.videoObserver = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) entry.target.querySelector('video')?.pause();
      }), { root: this.viewport, threshold: 0.35 });
      this.cards.forEach(card => this.videoObserver.observe(card));
      this.resize = new ResizeObserver(() => {
        this.fitRatingSummary();
        this.measure();
      });
      this.resize.observe(this.viewport);
      this.resize.observe(this.originals[0]);
      if (this.ratingSummary) this.resize.observe(this.ratingSummary.parentElement);
      this.updateAutoplayControl();
      this.fitRatingSummary();
      this.measure(true);
      document.fonts?.ready.then(() => {
        if (!this.isConnected) return;
        this.fitRatingSummary();
        this.measure();
      });
    }
    measure(force = false) {
      const width = this.viewport.getBoundingClientRect().width;
      const cardWidth = this.originals[0].getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(this).getPropertyValue('--rc-gap')) || 0;
      const key = `${width}:${cardWidth}:${gap}`;
      if (!width || !cardWidth || (!force && key === this.geometryKey)) return;
      this.geometryKey = key;
      this.cancelMotion();
      this.releasePointer();
      this.stride = cardWidth + gap;
      this.viewport.scrollLeft = 0;
      this.draw(this.wrap(Math.round(this.position)));
      this.setActive(this.nearestLogical());
      this.syncAutoplay(true);
    }
    wrap(position) {
      if (!this.loop) return this.constrain(position);
      return (position % this.period + this.period) % this.period;
    }
    constrain(position) {
      return this.loop ? position : Math.max(0, Math.min(this.count - 1, position));
    }
    draw(position) {
      this.position = position;
      this.cards.forEach((card, i) => {
        let offset = i - position;
        if (this.loop) offset = ((offset + this.period / 2) % this.period + this.period) % this.period - this.period / 2;
        card.style.transform = `translate3d(${offset * this.stride}px,0,0)`;
      });
    }
    nearestLogical(position = this.position) {
      return (Math.round(position) % this.count + this.count) % this.count;
    }
    cancelMotion() {
      cancelAnimationFrame(this.motionFrame);
      this.moving = false;
      this.targetPosition = null;
      this.targetLogical = null;
    }
    go(logical, smooth = true) {
      logical = this.loop ? (logical % this.count + this.count) % this.count : this.constrain(logical);
      const target = this.loop ? logical + Math.round((this.position - logical) / this.count) * this.count : logical;
      this.moveTo(target, smooth);
    }
    moveTo(target, smooth = true, settling = false) {
      target = this.constrain(Math.round(target));
      this.cancelMotion();
      this.targetPosition = target;
      this.targetLogical = this.nearestLogical(target);
      const from = this.position;
      const finish = () => {
        this.draw(this.wrap(target));
        this.viewport.scrollLeft = 0;
        this.moving = false;
        this.targetPosition = null;
        this.targetLogical = null;
        this.setActive(this.nearestLogical());
        this.syncAutoplay(true);
      };
      if (!smooth || this.motionPreference.matches || Math.abs(target - from) < 0.001) { finish(); return; }
      this.moving = true;
      this.syncAutoplay();
      this.setActive(this.nearestLogical(target));
      const duration = settling ? Math.max(180, Math.min(340, Math.abs(target - from) * 350)) : 460;
      let started;
      const move = time => {
        if (started === undefined) started = time;
        const progress = Math.min(1, (time - started) / duration);
        const eased = settling ? 1 - (1 - progress) ** 3 : (1 - Math.cos(Math.PI * progress)) / 2;
        this.draw(from + (target - from) * eased);
        if (progress < 1) this.motionFrame = requestAnimationFrame(move);
        else finish();
      };
      this.motionFrame = requestAnimationFrame(move);
    }
    step(delta) {
      const target = (this.targetPosition ?? Math.round(this.position)) + delta;
      if (!this.loop && (target < 0 || target >= this.count)) return;
      this.moveTo(target);
    }
    pointerStart(event) {
      if (!this.stride || event.isPrimary === false || (event.pointerType === 'mouse' && event.button !== 0) || this.pointerId !== null) return;
      this.cancelMotion();
      clearTimeout(this.wheelTimer);
      this.wheeling = false;
      this.pointerId = event.pointerId;
      this.pointerStartX = event.clientX;
      this.pointerStartY = event.clientY;
      this.pointerStartPosition = this.position;
      this.pointerVelocity = 0;
      this.lastPointerTime = performance.now();
      this.pointerSamples = [{ x: event.clientX, time: this.lastPointerTime }];
      this.dragging = false;
      this.syncAutoplay();
    }
    pointerMove(event) {
      if (event.pointerId !== this.pointerId) return;
      const dx = event.clientX - this.pointerStartX;
      const dy = event.clientY - this.pointerStartY;
      if (!this.dragging) {
        if (event.pointerType === 'mouse' && Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx) * 1.15) {
          this.pointerFinish(event, true);
          return;
        }
        if (Math.abs(dx) < 7 || Math.abs(dx) <= Math.abs(dy)) return;
        this.dragging = true;
        this.viewport.setPointerCapture(event.pointerId);
        this.classList.add('rc--dragging');
      }
      if (event.cancelable) event.preventDefault();
      const now = performance.now();
      this.pointerSamples.push({ x: event.clientX, time: now });
      while (this.pointerSamples.length > 2 && now - this.pointerSamples[1].time > 100) this.pointerSamples.shift();
      const first = this.pointerSamples[0];
      this.pointerVelocity = Math.max(-1.2, Math.min(1.2, (first.x - event.clientX) / Math.max(1, now - first.time)));
      this.lastPointerTime = now;
      this.draw(this.constrain(this.pointerStartPosition - dx / this.stride));
      this.setActive(this.nearestLogical());
    }
    releasePointer() {
      const pointer = this.pointerId;
      this.pointerId = null;
      this.dragging = false;
      this.classList.remove('rc--dragging');
      if (pointer !== null && this.viewport.hasPointerCapture(pointer)) this.viewport.releasePointerCapture(pointer);
    }
    pointerFinish(event, cancelled = false) {
      if (event.pointerId !== this.pointerId) return;
      if (event.type === 'lostpointercapture' && event.target !== this.viewport) return;
      const dragged = this.dragging;
      const delta = this.position - this.pointerStartPosition;
      const velocity = performance.now() - this.lastPointerTime < 100 ? this.pointerVelocity : 0;
      const momentum = cancelled ? 0 : Math.max(-0.3, Math.min(0.3, velocity * 120 / this.stride));
      let target = Math.round(this.position + momentum);
      const start = Math.round(this.pointerStartPosition);
      const distance = Math.abs(delta * this.stride);
      const swipe = event.pointerType === 'mouse'
        ? Math.abs(delta) > 0.15 || Math.abs(velocity) > 0.35
        : distance >= 20 || (distance >= 10 && Math.abs(velocity) > 0.25);
      if (dragged && !cancelled && target === start && swipe) target += Math.sign(delta || velocity);
      this.releasePointer();
      if (dragged) this.suppressClickUntil = performance.now() + 300;
      this.moveTo(target, true, dragged);
    }
    setActive(logical) {
      const active = this.wrap(Math.round(this.targetPosition ?? this.position));
      if (this.index === logical && this.activePhysical === active) return;
      this.index = logical;
      this.activePhysical = active;
      this.querySelectorAll('[data-dot]').forEach(dot => dot.setAttribute('aria-current', String(Number(dot.dataset.dot) === logical)));
      const previous = this.querySelector('[data-prev]'), next = this.querySelector('[data-next]');
      if (previous) previous.disabled = !this.loop && logical === 0;
      if (next) next.disabled = !this.loop && logical === this.count - 1;
      this.cards.forEach((card, i) => {
        card.setAttribute('aria-hidden', String(i !== active));
        card.querySelectorAll('button,a,[tabindex]').forEach(control => control.setAttribute('tabindex', i === active ? '0' : '-1'));
        if (Number(card.dataset.logical) !== logical) card.querySelector('video')?.pause();
      });
    }
    fitRatingSummary() {
      const row = this.ratingSummary;
      if (!row) return;
      const style = getComputedStyle(row);
      if (style.getPropertyValue('--rc-summary-auto-fit').trim() !== '1') { row.style.removeProperty('--rc-summary-fit-size'); return; }
      const available = row.parentElement.getBoundingClientRect().width - 0.5;
      const content = row.getBoundingClientRect().width;
      const current = parseFloat(style.fontSize);
      if (available <= 0 || content <= 0 || !Number.isFinite(current)) return;
      const next = Math.floor(Math.min(19, current * available / content) * 100) / 100;
      if (Math.abs(next - current) > 0.015) row.style.setProperty('--rc-summary-fit-size', `${next}px`);
    }
    canAutoplay() {
      const focused = document.activeElement;
      const focusedControl = this.contains(focused) && focused !== this.autoplayToggle && focused.matches(':focus-visible');
      return this.autoplayEnabled && !this.autoplayPaused && !this.editorSelected && this.inView && !document.hidden && this.pointerId === null && !this.dragging && !this.wheeling && !this.hovering && !this.moving && !focusedControl && ![...this.querySelectorAll('video')].some(video => !video.paused && !video.ended);
    }
    syncAutoplay(reset = false) {
      if (reset || !this.canAutoplay()) { clearTimeout(this.autoplayTimer); this.autoplayTimer = null; }
      if (this.autoplayTimer || !this.canAutoplay()) return;
      this.autoplayTimer = setTimeout(() => {
        this.autoplayTimer = null;
        if (!this.canAutoplay()) return;
        if (!this.loop && this.index === this.count - 1) this.go(0);
        else this.step(1);
      }, this.autoplayDelay);
    }
    updateAutoplayControl() {
      if (!this.autoplayToggle) return;
      this.autoplayToggle.setAttribute('aria-label', this.autoplayPaused ? this.autoplayToggle.dataset.resumeLabel : this.autoplayToggle.dataset.pauseLabel);
      this.autoplayToggle.setAttribute('aria-pressed', String(this.autoplayPaused));
      this.autoplayToggle.querySelector('[data-rotation-pause]').hidden = this.autoplayPaused;
      this.autoplayToggle.querySelector('[data-rotation-play]').hidden = !this.autoplayPaused;
    }
    syncTyping() {
      if (!this.typingText || this.typingTerms.length < 2) return;
      const allowed = this.inView && !document.hidden && !this.motionPreference.matches && !this.autoplayPaused;
      this.classList.toggle('rc--typing-active', allowed);
      if (!allowed) {
        clearTimeout(this.typingTimer);
        this.typingTimer = null;
        this.typingText.textContent = this.typingTerms[this.typingIndex];
        this.typingErasing = true;
        return;
      }
      if (!this.typingTimer) this.typingTimer = setTimeout(() => this.typeNext(), 1800);
    }
    typeNext() {
      this.typingTimer = null;
      const term = this.typingTerms[this.typingIndex];
      const length = this.typingText.textContent.length;
      let delay;
      if (this.typingErasing) {
        if (length) { this.typingText.textContent = term.slice(0, length - 1); delay = 42; }
        else { this.typingIndex = (this.typingIndex + 1) % this.typingTerms.length; this.typingErasing = false; delay = 260; }
      } else {
        this.typingText.textContent = term.slice(0, length + 1);
        if (length + 1 >= term.length) { this.typingErasing = true; delay = 1800; }
        else delay = 78;
      }
      this.typingTimer = setTimeout(() => this.typeNext(), delay);
    }
    videoState(card, video) {
      const playing = !video.paused && !video.ended;
      card.querySelector('.rc__play').hidden = playing;
      card.querySelector('.rc__pause').hidden = !playing;
      const mute = card.querySelector('[data-mute]');
      mute.hidden = !playing;
      mute.setAttribute('aria-pressed', String(video.muted));
      mute.setAttribute('aria-label', video.muted ? this.dataset.unmute : this.dataset.mute);
      this.syncAutoplay();
    }
    disconnectedCallback() {
      if (!this.abort) return;
      this.autoplayEnabled = false;
      clearTimeout(this.autoplayTimer);
      clearTimeout(this.typingTimer);
      clearTimeout(this.wheelTimer);
      this.autoplayTimer = this.typingTimer = null;
      this.cancelMotion();
      this.abort.abort();
      this.abort = null;
      this.visibilityObserver?.disconnect();
      this.videoObserver?.disconnect();
      this.resize?.disconnect();
      this.querySelectorAll('video').forEach(video => video.pause());
      this.querySelectorAll('[data-clone]').forEach(card => card.remove());
      this.track.style.transform = '';
      this.originals.forEach(card => {
        card.style.removeProperty('transform');
        card.removeAttribute('aria-hidden');
        card.querySelectorAll('button,a,[tabindex]').forEach(control => control.removeAttribute('tabindex'));
      });
      this.classList.remove('rc--loop', 'rc--enhanced', 'rc--dragging', 'rc--typing-active');
      this.geometryKey = null;
    }
  });
}
